import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';
import { toast } from 'sonner';
// eslint-disable-next-line import/named
import { v4 as uuid } from 'uuid';

import {
  IBasket,
  IBasketDeliveryAddress,
  IBasketLine,
  IBasketPatchLine,
  IBasketUpdateB2C,
} from '@boss/services/client';
import { DeepPartial } from '@boss/types/b2b-b2c';

import {
  addBasketLines,
  addVoucherToBasket,
  deleteBasketLine,
  deleteVoucherFromBasket,
  getBasket,
  getBasketById,
  getDeliveryDates,
  patchBasket,
  updateBasketB2B,
  updateBasketB2C,
  updateBasketLine,
} from './connector';
import { useAnonymousBasket, useOptimistic, useProfile } from '../../hooks';
import { isB2b, mapNewBasketLineToMockBasketLine } from '../../utils';

import { basketKeys } from '.';

const useGetBasket = (
  basketId?: string,
  enabled = true,
  options?: {
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
  },
) => {
  const { t } = useTranslation('common');
  const { accountId } = useProfile();
  const { setAnonymousBasketId, anonymousBasketId } = useAnonymousBasket();
  const { locale } = useRouter();
  const _basketId = basketId || (anonymousBasketId ?? undefined);

  return useQuery<IBasket>({
    queryKey: basketKeys.basket(accountId, _basketId),
    queryFn: async () => {
      if (_basketId) {
        return await getBasketById({ basketId: _basketId, locale: locale as string });
      }

      if (accountId) {
        return await getBasket({ accountId, locale: locale as string });
      }

      const basket = await getBasket({ locale: locale as string });

      if (basket?.id) {
        setAnonymousBasketId(basket.id);
      }

      return basket;
    },
    enabled: isB2b ? !!accountId && enabled : enabled,
    refetchOnMount: options?.refetchOnMount,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchOnReconnect: options?.refetchOnReconnect,
    meta: {
      errorMessage: t('toast.fetchBasket.error.title'),
    },
  });
};

// Helper to simulate different error responses
const mockErrorResponse = (status: number, statusText: string) => {
  throw new Response(statusText, {
    status,
    statusText,
  });
};

const useCheckBasketProducts = (basketId?: string, mockError?: { status: number; statusText: string }) => {
  const { locale } = useRouter();
  const { anonymousBasketId } = useAnonymousBasket();
  const _basketId = basketId || (anonymousBasketId ?? undefined);

  return useQuery<IBasket>(
    ['checkBasketProducts', basketId],
    async () => {
      // Simulate error if mockError is provided
      if (mockError) {
        mockErrorResponse(mockError.status, mockError.statusText);
      }

      if (!_basketId) {
        throw new Error('No basket ID available');
      }

      return await getBasketById({ basketId: _basketId, locale: locale as string, checkProducts: true });
    },
    {
      retry: false,
      enabled: false,
    },
  );
};

const useGetBasketById = () => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ basketId }: { basketId: string }) => {
      try {
        return await getBasketById({ basketId, locale: locale as string });
      } catch (error) {
        return error;
      }
    },
    onError: () => {
      toast.error(t('toast.fetchBasketById.error.title'));
    },
  });
};

/**
 * Verify naively update the basket to increase the amount of lines present in the basket when the given newLine skuid doesn't exist yet
 **/
const getOptimisticBasketLines = (
  lines: DeepPartial<IBasketLine>[],
  newLines: DeepPartial<IBasketLine>[],
): DeepPartial<IBasketLine>[] => {
  const filtedNewLines = newLines.filter(newLine => !lines.some(line => line?.item?.skuid === newLine?.item?.skuid));

  // Adding of a mock basketline to ensure that when no lines are present, a new one is added temporarily until the basket gets refetched
  return [...lines, ...filtedNewLines.map(mapNewBasketLineToMockBasketLine)];
};

/**
 * useMutation implementation to add a new basket line
 *
 * @returns {object} An object with a mutate function and mutation status
 */
const useAddBasketLine = () => {
  const { t } = useTranslation('common');
  const { onMutateHelper } = useOptimistic();
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({
      basketId,
      newBasketLines,
    }: {
      basketId: string;
      newBasketLines: DeepPartial<IBasketLine>[];
      showSuccessToast?: boolean;
    }) => {
      return addBasketLines({ basketId, newBasketLines, locale: locale as string });
    },
    onMutate: async ({ basketId, newBasketLines }) => {
      const queryKey = basketKeys.basket(accountId, basketId);
      const previousData: IBasket | undefined = queryClient.getQueryData(queryKey);
      const previousLines = previousData?.lines ?? [];
      const newBasket = { ...previousData, lines: getOptimisticBasketLines(previousLines, newBasketLines) };

      return onMutateHelper(queryKey, previousData, newBasket);
    },
    onError: (_, variables, context) => {
      const queryKey = basketKeys.basket(accountId, variables.basketId);

      queryClient.setQueryData(queryKey, context?.previousData);
      toast.error(t('toast.addBasketLine.error.title'));
    },
    onSuccess: (data, params) => {
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, params.basketId), data);

      if (isB2b || params.showSuccessToast) {
        toast.success(t('toast.addBasketLine.success.title', { count: params.newBasketLines.length }));
      }
    },
  });
};

/**
 * useMutation implementation to add a new basket line
 *
 * @param fallbackUrl When the last line of the basket is deleted, a fallback url can be used for re-routing
 * @returns {object} An object with a mutate function and mutation status
 */
const useDeleteBasketLine = (fallbackUrl?: string) => {
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const router = useRouter();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ basketId, basketLineId }: { basketId: string; basketLineId: string }) => {
      return deleteBasketLine({ basketId, basketLineId, locale: locale as string });
    },
    onSuccess: (data, variables) => {
      if (fallbackUrl && data?.lines.length === 0) {
        router.push(fallbackUrl);
      }
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
    },
  });
};

/**
 * useMutation implementation to update a basket line
 *
 * @returns {object} An object with a mutate function and mutation status
 */
const useUpdateBasketLine = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();
  const latestRequestRef = useRef<string | null>(null);

  return useMutation({
    mutationFn: async ({ basketId, basketLine }: { basketId: string; basketLine: DeepPartial<IBasketLine> }) => {
      return updateBasketLine({ basketId, basketLine, locale: locale as string });
    },
    onMutate: async ({ basketId, basketLine }) => {
      const queryKeyWithBasketId = basketKeys.basket(accountId, basketId);
      const queryKeyWithoutBasketId = basketKeys.basket(accountId);
      const requestId = uuid();

      latestRequestRef.current = requestId;

      // Try to get previous data from either cache key
      const previousData: IBasket | undefined =
        queryClient.getQueryData(queryKeyWithBasketId) || queryClient.getQueryData(queryKeyWithoutBasketId);

      const newBasket = produce(previousData, draft => {
        if (draft) {
          (draft.lines as DeepPartial<IBasketLine>[]) = draft.lines.map(line => {
            if (line.id === basketLine.id) {
              return basketLine;
            }
            return line;
          });
        }
      });

      // Update both cache keys for optimistic update
      queryClient.setQueryData(queryKeyWithoutBasketId, newBasket);
      queryClient.setQueryData(queryKeyWithBasketId, newBasket);

      return { previousData, requestId, newBasket };
    },
    onError: (_, variables, context) => {
      // Restore previous data to both cache keys
      queryClient.setQueryData(basketKeys.basket(accountId), context?.previousData);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), context?.previousData);
      toast.error(t('toast.updateBasketLine.error.title'));
    },
    onSuccess: (data, variables, context) => {
      if (context?.requestId === latestRequestRef.current) {
        // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
        queryClient.setQueryData(basketKeys.basket(accountId), data);
        queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
      }
    },
  });
};

/**
 * useMutation implemntation to update the b2b basket
 * @returns {object} An object with a mutate function and mutation status
 */
const useUpdateBasketB2B = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ basketId, basket }: { basketId: string; basket: DeepPartial<IBasket> }) => {
      return updateBasketB2B(basketId, basket, locale as string);
    },
    onSuccess: (data, variables) => {
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
    },
    onError: (error: Error) => {
      toast.error(t('toast.updateBasket.error.title'));
      throw error;
    },
  });
};

/**
 * useMutation implementation to update the b2c basket
 */
const useUpdateBasketB2C = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ basketId, basket }: { basketId: string; basket: DeepPartial<IBasketUpdateB2C> }) => {
      return updateBasketB2C({ basketId, basket, locale: locale as string });
    },
    onSuccess: (data, variables) => {
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
    },
    onError: () => {
      toast.error(t('toast.updateBasket.error.title'));
    },
  });
};

/**
 * useMutation implementation to patch the basket
 */
const usePatchBasket = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ basketId, patchLines }: { basketId: string; patchLines: IBasketPatchLine[] }) => {
      return patchBasket(basketId, patchLines, locale as string);
    },
    onSuccess: (data, variables) => {
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
    },
    onError: () => {
      toast.error(t('toast.patchBasket.error.title'));
    },
  });
};

/**
 * useMutation implementation to fetch delivery dates for the basket
 * @param modeOfDelivery
 * @returns
 */
const useGetDeliveryDates = () => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({
      modeOfDelivery,
      storeId,
      address,
    }: {
      storeId?: string;
      modeOfDelivery: number;
      address?: IBasketDeliveryAddress;
    }) =>
      await getDeliveryDates({
        modeOfDelivery,
        storeId,
        address,
        locale: locale as string,
      }),
    meta: {
      errorMessage: t('toast.fetchDeliveryDates.error.title'),
    },
  });
};

/**
 * useMutation implementation to add a voucher to the basket
 */
const useAddVoucherToBasket = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({
      basketId,
      voucher,
    }: {
      basketId: string;
      voucher: { type: string; reference: string; webcode?: string };
    }) => {
      return addVoucherToBasket({ basketId, voucher, locale: locale as string });
    },
    onSuccess: (data, variables) => {
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
      toast.success(t('toast.addVoucherToBasket.success.title'));
    },
    onError: () => {
      toast.error(t('toast.addVoucherToBasket.error.title'));
    },
  });
};

/**
 * useMutation implementation to delete a voucher from the basket
 */
const useDeleteVoucherFromBasket = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { accountId } = useProfile();
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ basketId, voucherId }: { basketId: string; voucherId: string }) => {
      return deleteVoucherFromBasket({ basketId, voucherId, locale: locale as string });
    },
    onSuccess: (data, variables) => {
      // Keep cache in sync for both logged-in (no basketId) and anonymous (basketId) flows.
      queryClient.setQueryData(basketKeys.basket(accountId), data);
      queryClient.setQueryData(basketKeys.basket(accountId, variables.basketId), data);
      toast.success(t('toast.deleteVoucherFromBasket.success.title'));
    },
    onError: () => {
      toast.error(t('toast.deleteVoucherFromBasket.error.title'));
    },
  });
};

export {
  useAddVoucherToBasket,
  useDeleteVoucherFromBasket,
  useAddBasketLine,
  useDeleteBasketLine,
  useGetBasket,
  useGetBasketById,
  useUpdateBasketLine,
  useGetDeliveryDates,
  useUpdateBasketB2B,
  useUpdateBasketB2C,
  usePatchBasket,
  useCheckBasketProducts,
};
